import React, { useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { BaseComponentsConfig, ButtonConfig } from './types'
import { BaseButton } from './BaseButton'
import {
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable,
} from 'react-beautiful-dnd'
import { QuestionnaireContext } from './context'
import { FieldTypeEnum } from 'packages/sdks-next/growp'
import { baseConfigQenerator, getUsedUserIds, typeToLabel } from './utils'
import { xor } from '@seiue/util'
import { Toast } from '@seiue/ui'
import { AnyValueObj } from 'packages/types'
import { createUuid } from '@seiue/react-flow-builder'

/**
 * 问卷 - 工具栏Button渲染
 *
 * @param param - 参数
 * @param param.configs - 配置
 * @param param.isCommonField - 是否是通用字段
 * @returns React.ReactNode
 */
export const ButtonRender: React.FC<{
  configs: BaseComponentsConfig[]
  isCommonField?: boolean
}> = ({ configs, isCommonField }) => {
  const {
    questionnaires,
    setQuestionnaires,
    activeItemIndex,
    userFieldsConfig,
    allGroupIds,
    structIsChange,
    setActiveItemIndex,
  } = useContext(QuestionnaireContext)

  const timer = useRef<NodeJS.Timeout>()

  useEffect(() => {
    return () => {
      timer.current && clearTimeout(timer.current)
    }
  }, [])

  const getRenderItem = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric,
    btnConfigs: ButtonConfig[]
  ) => {
    const btnConfig = btnConfigs[rubric.source.index]
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={provided.draggableProps.style}
        className={snapshot.isDragging ? 'dragging' : ''}
      >
        <BaseButton
          key={btnConfig.id}
          name={btnConfig.iconName}
          text={btnConfig.text}
        />
      </div>
    )
  }

  const insertItem = (
    type: FieldTypeEnum,
    groupName: string,
    index: number
  ) => {
    let dragField: AnyValueObj = {}
    let isUsed = false
    if (!isCommonField) {
      userFieldsConfig.map((field) => {
        if (field.groupName === groupName) {
          dragField = field.buttonConfigs[index]

          const usedIds = getUsedUserIds(questionnaires)
          const remainingIds = xor(usedIds, allGroupIds)
          if (remainingIds.includes(dragField[`id`])) {
            isUsed = true
          }
        }
      })
    }
    if (isUsed) {
      Toast.warning('同一用户字段只能使用一次')
      return
    }
    const preQuestionnaires = questionnaires || []
    const newItem = {
      uuid: createUuid(),
      ...baseConfigQenerator(type),
      type,
      label: typeToLabel.get(type),
      ...dragField,
      require: false,
      children: (type === FieldTypeEnum.Product
        ? [
            {
              ...baseConfigQenerator(FieldTypeEnum.String),
              label: typeToLabel.get(FieldTypeEnum.String),
            },
          ]
        : undefined) as any,
    }

    const insertIndex =
      activeItemIndex === undefined
        ? preQuestionnaires.length
        : activeItemIndex + 1
    preQuestionnaires.splice(insertIndex, 0, newItem)
    setQuestionnaires([...preQuestionnaires])
    if (
      ![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(
        type as FieldTypeEnum
      )
    ) {
      structIsChange && structIsChange()
    }
    setActiveItemIndex(insertIndex)
    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const ele = document.getElementById(`${insertIndex}`)
      if (ele) {
        ele.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        })
        ele.focus()
      }
    }, 200)
  }

  return (
    <Wrapper>
      {configs.map((config) => {
        return (
          <Droppable
            droppableId={config.groupName}
            key={config.groupName}
            renderClone={(provided, snapshot, rubric) =>
              getRenderItem(provided, snapshot, rubric, config.buttonConfigs)
            }
            isDropDisabled
            direction="horizontal"
          >
            {(provided, snapshot) => {
              return (
                <div
                  key={config.groupName}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <GroupName>{config.groupName}</GroupName>
                  <ButtonWrapper>
                    {config.buttonConfigs.map((btnConfig, index) => {
                      const shouldRenderClone = isCommonField
                        ? btnConfig.type === snapshot.draggingFromThisWith
                        : `${btnConfig.type}-${btnConfig.id}` ===
                          snapshot.draggingFromThisWith
                      return (
                        <React.Fragment key={btnConfig.id}>
                          {shouldRenderClone ? (
                            <div className="react-beatiful-dnd-copy">
                              <BaseButton
                                key={btnConfig.id}
                                name={btnConfig.iconName}
                                text={btnConfig.text}
                              />
                            </div>
                          ) : (
                            <Draggable
                              draggableId={
                                isCommonField
                                  ? btnConfig.type
                                  : `${btnConfig.type}-${btnConfig.id}`
                              }
                              index={index}
                            >
                              {(provided, snapshot) => {
                                return (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={
                                      snapshot.isDragging ? 'dragging' : ''
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      insertItem(
                                        btnConfig.type as FieldTypeEnum,
                                        config.groupName,
                                        index
                                      )
                                    }}
                                  >
                                    <BaseButton
                                      name={btnConfig.iconName}
                                      text={btnConfig.text}
                                    />
                                  </div>
                                )
                              }}
                            </Draggable>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </ButtonWrapper>
                </div>
              )
            }}
          </Droppable>
        )
      })}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  /* height: calc(100vh - 182px); */
  overflow: auto;
  padding: 0 20px;

  ::-webkit-scrollbar {
    display: none;
  }
  .react-beatiful-dnd-copy ~ div {
    transform: none !important;
  }
  /* 拖动时的样式 */
  .dragging {
    background: yellow;
  }

  /* 拖动时复制停留在原来空间的样式 */
  .react-beatiful-dnd-copy {
    border-radius: 8px;
  }
  .react-beatiful-dnd-copy ~ [data-rbd-placeholder-context-id] {
    display: none !important;
  }
`

const GroupName = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.text._3};
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
`
